import styled from 'styled-components';
import { RefObject } from 'react';
import { Trans } from 'react-i18next';

import { EmailNewsletter } from 'layout/footer/components/EmailNewsletter';
import { FooterNavigationDesktop } from 'layout/footer/components/FooterNavigationDesktop';
import { FooterNavigationMobile } from 'layout/footer/components/FooterNavigationMobile';
import { t } from 'localization/utils/t';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useSitePreferencesModal } from 'shared/hooks/useSitePreferencesModal';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';

const TP = 'layout.footer.components.Footer';

interface IFooterProps {
  footerRef?: RefObject<HTMLElement>;
  setIsBuyBarVisible?: (val: boolean) => void;
  isInApp?: boolean;
}

const Footer = ({ footerRef, setIsBuyBarVisible, isInApp }: IFooterProps) => {
  const { isMobile } = useWindowSize();
  const isMounted = useIsMounted();
  const careersLink = 'https://boards.greenhouse.io/goatgroup';

  const { openSitePreferencesModal } = useSitePreferencesModal();

  const footerData = [
    {
      id: 1,
      title: t(`${TP}.about`, 'About'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.aboutUs`, 'About Us'),
          slug: 'about-us',
          openInNewTab: false,
        },
        {
          id: 2,
          name: t(`${TP}.careers`, 'Careers'),
          externalUrl: careersLink,
          openInNewTab: true,
        },
        {
          id: 3,
          name: t(`${TP}.goatApp`, 'GOAT App'),
          slug: 'app',
          openInNewTab: false,
        },
      ],
    },
    {
      id: 2,
      title: t(`${TP}.buy`, 'Buy'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.assuranceOfAuthenticity`, 'Assurance of Authenticity'),
          slug: 'verification',
          openInNewTab: true,
        },
        {
          id: 2,
          name: t(`${TP}.buyerProtectionPolicy`, 'Buyer Protection Policy'),
          slug: 'buyer-protection-guarantee',
          openInNewTab: true,
        },
        {
          id: 3,
          name: t(`${TP}.goatClean`, 'GOAT Clean'),
          slug: 'goat-clean',
          openInNewTab: false,
        },
        {
          id: 4,
          name: t(`${TP}.giftCards`, 'Gift Cards'),
          slug: 'gift-cards',
          openInNewTab: false,
        },
        {
          id: 5,
          name: t(`${TP}.shippingAndReturns`, 'Shipping and Returns'),
          slug: 'returns',
          openInNewTab: true,
        },
      ],
    },
    {
      id: 3,
      title: t(`${TP}.sell`, 'Sell'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.sellingOnGoat`, 'Selling on GOAT'),
          slug: 'selling',
          openInNewTab: true,
        },
        {
          id: 2,
          name: t(`${TP}.feePolicy`, 'Fee Policy'),
          slug: 'fees',
          openInNewTab: true,
        },
        {
          id: 3,
          name: t(`${TP}.dropOff`, 'Drop Off'),
          slug: 'goat-group-dropoff-policy',
          openInNewTab: true,
        },
      ],
    },
    {
      id: 5,
      title: t(`${TP}.support`, 'Support'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.goatSupport`, 'GOAT Support'),
          openInNewTab: true,
          externalUrl: 'https://support.goat.com/hc/en-us',
        },
        {
          id: 2,
          name: t(`${TP}.checkGiftCardBalance`, 'Check Gift Card Balance'),
          slug: 'gift-cards/balance',
          openInNewTab: true,
        },
      ],
    },
  ];

  const mobileFooterData = [
    {
      id: 6,
      title: t(`${TP}.termsAndPreferences`, 'Terms and Preferences'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.terms`, 'Terms'),
          slug: 'terms',
          openInNewTab: true,
        },
        {
          id: 2,
          name: t(`${TP}.yourPrivacyChoices`, 'Your Privacy Choices'),
          slug: 'privacy',
          openInNewTab: true,
        },
        {
          id: 3,
          name: t(`${TP}.locationPreferences`, 'Location Preferences'),
          onClick: openSitePreferencesModal,
        },
        {
          id: 4,
          name: t(`${TP}.accessibility`, 'Accessibility'),
          slug: 'accessibility',
        },
        {
          id: 5,
          name: t(`${TP}.sitemap`, 'Sitemap'),
          slug: 'sitemap-html',
        },
      ],
    },
  ];

  // we check for isMounted so that the footer does not appear before the body of the page
  // is ready, causing layout shift
  if (isInApp || !isMounted()) {
    return null;
  }

  return (
    <Footer.Wrapper id="site-footer" ref={footerRef}>
      <Footer.FlexWrapper>
        <Footer.NewsLetterWrapper>
          <EmailNewsletter setIsBuyBarVisible={setIsBuyBarVisible} />
        </Footer.NewsLetterWrapper>
        <Footer.LinksWrapper>
          {isMobile ? (
            <FooterNavigationMobile footerLinks={[...footerData, ...mobileFooterData]} />
          ) : (
            <FooterNavigationDesktop footerLinks={footerData} />
          )}
        </Footer.LinksWrapper>

        <Footer.MobileAppDownload>
          <Footer.DownloadAppText>
            {t(
              `${TP}.downloadAppText`,
              'Never miss a drop again. Download the GOAT app for reminders and exclusive promotions.',
            )}
          </Footer.DownloadAppText>
          <Footer.DownloadAppButton
            href="https://goat.app.link/C8eM8PorVbb"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t(`${TP}.downloadApp`, 'Download App')}
          </Footer.DownloadAppButton>
        </Footer.MobileAppDownload>
      </Footer.FlexWrapper>

      <Footer.Copyright>
        <Trans
          i18nKey={`${TP}.copyright`}
          defaults="<0>© {year}</0><0> 1661, Inc. All Rights Reserved</0>"
          components={[<span />]}
          values={{ year: new Date().getFullYear() }}
        />
      </Footer.Copyright>
    </Footer.Wrapper>
  );
};

Footer.Wrapper = styled.footer`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  padding-top: 64px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    justify-content: space-between;
    align-items: flex-start;
    padding: 64px 32px 24px;
  }
`;

Footer.FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: row;
    min-height: 81px;
  }
`;

Footer.NewsLetterWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    order: 2;
  }
`;

Footer.LinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: row;
    height: fit-content;
  }
`;

Footer.MobileAppDownload = styled.div`
  color: ${({ theme }) => theme.navigation.downloadAppText};
  margin: 40px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

Footer.DownloadAppText = styled.div`
  margin-bottom: 24px;
  line-height: 16px;
  ${variableFontRegular({ size: '12px', height: '16px' })};
`;

Footer.DownloadAppButton = styled.a`
  color: ${({ theme }) => theme.navigation.invertedTextColor};
  background-color: ${({ theme }) => theme.navigation.downloadAppBackground};
  padding: 12px 15px 10px;
  border-radius: ${({ theme }) => theme.borderRadii.button};

  ${variableFontRegular({ size: '11px', height: '14px' })};

  &:hover {
    color: ${({ theme }) => theme.navigation.invertedTextColor};
  }
`;

Footer.Copyright = styled.div`
  margin: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 0;
  }
`;

export default Footer;
